import { createApi } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import type { AxiosRequestConfig } from "axios";
import Axios from "axios";
import { axiosNewPrivate } from "../../util/axios";
import {
  getMockAccessCode,
  getMockUsageLog,
  mockCheckIn,
  mockFinishUsage,
  mockSaveReturnPhoto,
} from "../../util/usageLogMock";

export type UsageStatus = "booked" | "active" | "overtime" | "finished";

/**
 * Which access method the booked asset's access point uses (plan A3 — the mode
 * lives on the access point, per door, not on a tenant toggle):
 * - `smart_lock`: IoT door open (today's hardware path);
 * - `static_code`: the access step shows a just-in-time access code instead;
 * - `null`: the asset has no access point — the flow renders the no-hardware
 *   path for this booking;
 * - absent (`undefined`): backend doesn't serve the field yet — behave exactly
 *   like today (hardware door step), so the rollout is backward-compatible.
 */
export type AccessType = "smart_lock" | "static_code";

export interface UsageLog {
  orderId: string;
  accountId: string | null;
  status: UsageStatus;
  accessType?: AccessType | null;
  activatedAtDate?: string | null;
  finishedAtDate?: string | null;
  participantCount?: number | null;
  notes?: string | null;
}

/** Just-in-time access code for a `static_code` booking (plan A2). */
export interface AccessCode {
  code: string;
}

export interface GetAccessCodeArg {
  orderId: string;
  /**
   * Scanned/entered on-site location code, required by the backend when the
   * tenant has `locationCheck` enabled (plan A11 — server-side enforcement).
   */
  locationCode?: string;
}

export interface GetUsageLogArg {
  orderId: string;
}

export interface CheckInArg {
  orderId: string;
  participantCount?: number | null;
}

export interface FinishUsageArg {
  orderId: string;
  notes?: string | null;
}

export interface ReturnPhotoArg {
  orderId: string;
  /** The captured check-out photo. Omit (with `skipped: true`) when skipped. */
  photo?: File | null;
  /** True when the customer skipped the photo — records status 'skipped'. */
  skipped?: boolean;
}

/**
 * Describes one usage_log request in an axios/RTK-agnostic shape so the same
 * endpoint definitions can run against either the in-memory mock or the real
 * new-backend, depending on `REACT_APP_MOCK_USAGE_LOG`.
 */
interface UsageLogRequest {
  type: "get" | "checkIn" | "finish" | "returnPhoto" | "accessCode";
  orderId: string;
  participantCount?: number | null;
  notes?: string | null;
  photo?: File | null;
  skipped?: boolean;
  locationCode?: string;
}

const isMockMode = (): boolean =>
  process.env.REACT_APP_MOCK_USAGE_LOG === "true";

const MOCK_LATENCY_MS = 300;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const newBackendBase = (): string => {
  // Strip trailing slashes without a regex (keeps SonarCloud's regex hotspot
  // rule out of a hot path that only ever trims `/`).
  let base = process.env.REACT_APP_NEW_API_BASE_URL ?? "";
  while (base.endsWith("/")) base = base.slice(0, -1);
  return base;
};

const buildAxiosConfig = (req: UsageLogRequest): AxiosRequestConfig => {
  const base = newBackendBase();
  const root = `${base}/usage-logs/${req.orderId}`;
  switch (req.type) {
    case "get":
      return { url: root, method: "GET" };
    case "checkIn":
      return {
        url: `${root}/check-in`,
        method: "POST",
        data: { participantCount: req.participantCount ?? null },
      };
    case "finish":
      return {
        url: `${root}/finish`,
        method: "POST",
        data: { notes: req.notes ?? null },
      };
    case "returnPhoto": {
      const formData = new FormData();
      if (req.photo) formData.append("photo", req.photo);
      if (req.skipped) formData.append("skipped", "true");
      return {
        url: `${root}/return-photo`,
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      };
    }
    case "accessCode":
      return {
        url: `${root}/access-code`,
        method: "GET",
        ...(req.locationCode ? { params: { locationCode: req.locationCode } } : {}),
      };
  }
};

const runMock = async (req: UsageLogRequest): Promise<UsageLog | AccessCode> => {
  // Simulate ~300ms backend latency for realistic loading states.
  await delay(MOCK_LATENCY_MS);
  switch (req.type) {
    case "get":
      return getMockUsageLog(req.orderId);
    case "checkIn":
      return mockCheckIn(req.orderId, req.participantCount);
    case "finish":
      return mockFinishUsage(req.orderId, req.notes);
    case "returnPhoto":
      return mockSaveReturnPhoto(req.orderId);
    case "accessCode":
      return getMockAccessCode();
  }
};

/**
 * Custom base query for the usage_log slice. In mock mode it reads/writes the
 * localStorage-backed mock store; otherwise it calls the new-backend via the
 * shared `axiosNewPrivate` instance. The real path won't resolve until the
 * backend ships the `/usage-logs/*` endpoints — that is expected.
 */
const usageLogBaseQuery: BaseQueryFn<UsageLogRequest, unknown, unknown> =
  async (req) => {
    if (isMockMode()) {
      return { data: await runMock(req) };
    }
    try {
      const result = await axiosNewPrivate(buildAxiosConfig(req));
      return { data: result.data };
    } catch (err) {
      if (!Axios.isAxiosError(err)) {
        return { error: err };
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const usageLogApiSlice = createApi({
  reducerPath: "usageLogApiSlice",
  baseQuery: usageLogBaseQuery,
  tagTypes: ["UsageLog"],
  endpoints: (builder) => ({
    getUsageLog: builder.query<UsageLog, GetUsageLogArg>({
      query: ({ orderId }) => ({ type: "get", orderId }),
      providesTags: ["UsageLog"],
    }),
    checkIn: builder.mutation<UsageLog, CheckInArg>({
      query: ({ orderId, participantCount }) => ({
        type: "checkIn",
        orderId,
        participantCount,
      }),
      invalidatesTags: ["UsageLog"],
    }),
    finishUsage: builder.mutation<UsageLog, FinishUsageArg>({
      query: ({ orderId, notes }) => ({ type: "finish", orderId, notes }),
      invalidatesTags: ["UsageLog"],
    }),
    uploadReturnPhoto: builder.mutation<UsageLog, ReturnPhotoArg>({
      query: ({ orderId, photo, skipped }) => ({
        type: "returnPhoto",
        orderId,
        photo,
        skipped,
      }),
      invalidatesTags: ["UsageLog"],
    }),
    // The access code is a secret (plan A2): fetched lazily on tap, held in the
    // in-memory RTK store only, and dropped the moment the step unsubscribes
    // (`keepUnusedDataFor: 0`). Never persisted, never on another payload.
    getAccessCode: builder.query<AccessCode, GetAccessCodeArg>({
      query: ({ orderId, locationCode }) => ({
        type: "accessCode",
        orderId,
        locationCode,
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetUsageLogQuery,
  useCheckInMutation,
  useFinishUsageMutation,
  useUploadReturnPhotoMutation,
  useLazyGetAccessCodeQuery,
} = usageLogApiSlice;
